import React, { useState } from 'react';
import './RsiExtremesEntryConditionsBar.css';

/**
 * Entry condition status for a single condition
 */
export interface EntryCondition {
  is_rsi_oversold: boolean;
  is_volatility_acceptable: boolean;
  is_ranging_market: boolean;
  is_weak_trend: boolean;
  is_volatility_percentile_ok: boolean;
  is_volume_normal: boolean;
  all_conditions_met: boolean;
  conditions_met_count: number;
}

/**
 * Props for RsiExtremesEntryConditionsBar component
 */
export interface RsiExtremesEntryConditionsBarProps {
  /** Entry conditions data with boolean status for each condition */
  entryConditions?: EntryCondition;
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Whether an error occurred fetching data */
  isError?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Configuration for each condition section
 */
const CONDITION_CONFIG = [
  {
    key: 'is_rsi_oversold' as keyof EntryCondition,
    label: 'RSI Oversold (<30)',
    shortLabel: 'RSI',
  },
  {
    key: 'is_volatility_acceptable' as keyof EntryCondition,
    label: 'Volatility LOW/MEDIUM',
    shortLabel: 'VOL',
  },
  {
    key: 'is_ranging_market' as keyof EntryCondition,
    label: 'Ranging Market',
    shortLabel: 'MKT',
  },
  {
    key: 'is_weak_trend' as keyof EntryCondition,
    label: 'Weak Trend (<0.5)',
    shortLabel: 'TRD',
  },
  {
    key: 'is_volatility_percentile_ok' as keyof EntryCondition,
    label: 'Volatility <70th Percentile',
    shortLabel: 'V%',
  },
  {
    key: 'is_volume_normal' as keyof EntryCondition,
    label: 'No Volume Spike (<2x)',
    shortLabel: 'SPK',
  },
];

/**
 * RsiExtremesEntryConditionsBar Component
 * 
 * Displays a visual indicator of entry condition status for RSI Extremes trade groups.
 * Shows 6 sections (RSI, Volume, Market, Trend, Vol%, Volume) with color-coded status.
 * 
 * @example
 * <RsiExtremesEntryConditionsBar 
 *   entryConditions={data?.entry_conditions} 
 *   isLoading={query.isLoading}
 *   isError={query.isError} 
 * />
 */
const RsiExtremesEntryConditionsBar: React.FC<RsiExtremesEntryConditionsBarProps> = ({
  entryConditions,
  isLoading = false,
  isError = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Get conditions met count from API response
  const conditionsMetCount = entryConditions?.conditions_met_count ?? 0;

  // Don't render if error occurred
  if (isError) {
    return null;
  }

  // Loading state: show skeleton
  if (isLoading) {
    return (
      <div className={`rsi-extremes-entry-conditions-bar ${className}`}>
        <div className="entry-conditions-sections loading">
          {CONDITION_CONFIG.map((_, index) => (
            <div key={index} className="entry-condition-section skeleton">
              <div className="skeleton-shimmer"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // No data state
  if (!entryConditions) {
    return null;
  }

  return (
    <div className={`rsi-extremes-entry-conditions-bar ${className}`}>
      {/* Summary count */}
      <div className="entry-conditions-summary">
        {conditionsMetCount}/6 conditions met
      </div>

      {/* Conditions sections */}
      <div className="entry-conditions-sections">
        {CONDITION_CONFIG.map((config, index) => {
          const conditionValue = entryConditions[config.key];
          const isMet = conditionValue === true;
          const isUnmet = conditionValue === false;
          const isUnknown = conditionValue === null || conditionValue === undefined;

          let sectionClass = 'entry-condition-section';
          if (isMet) sectionClass += ' met';
          else if (isUnmet) sectionClass += ' unmet';
          else if (isUnknown) sectionClass += ' unknown';

          // Symbol to display
          let symbol = '?';
          if (isMet) symbol = '✓';
          else if (isUnmet) symbol = '✗';

          return (
            <div
              key={config.key}
              className={sectionClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              aria-label={`${config.label}: ${isMet ? 'met' : isUnmet ? 'not met' : 'unknown'}`}
              tabIndex={0}
            >
              <span className="condition-symbol">{symbol}</span>

              {/* Tooltip */}
              {hoveredIndex === index && (
                <div className="entry-condition-tooltip" role="tooltip">
                  {config.label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RsiExtremesEntryConditionsBar;
